/**
 * Response Formatter
 * Formats analysis results as natural language
 *
 * @module ResponseFormatter
 * Sprint 32: OOA/OOD Refactoring
 */

import {
    InfrastructureReport,
    Anomaly,
    SecurityFinding,
    CostRecommendation
} from '../types/ii-agent-types';

/**
 * Interface for response formatter
 */
export interface IResponseFormatter {
    formatInfrastructure(report: InfrastructureReport, location?: string | null): string;
    formatAnomalies(anomalies: Anomaly[]): string;
    formatSecurity(findings: SecurityFinding[]): string;
    formatCost(recommendations: CostRecommendation[]): string;
}

/**
 * Formatter for converting analysis results to natural language
 */
export class ResponseFormatter implements IResponseFormatter {

    /**
     * Format infrastructure report as natural language
     */
    formatInfrastructure(report: InfrastructureReport, location: string | null = null): string {
        const deviceTypes = Object.entries(report.device_breakdown || [])
            .map(([type, count]) => `${count} ${type}${count > 1 ? 's' : ''}`)
            .join(', ');

        const osTypes = Object.entries(report.os_distribution || [])
            .map(([os, count]) => `${count} ${os}`)
            .join(', ');

        const cloudProviders = Object.entries(report.cloud_provider_breakdown || [])
            .map(([provider, count]) => `${count} on ${provider}`)
            .join(', ');

        const title = location
            ? `**Infrastructure Overview for ${location}**`
            : `**Infrastructure Overview**`;

        return `${title}

**Total Assets**: ${report.total_devices} devices, ${report.total_applications} applications

**Health Score**: ${report.health_score.toFixed(1)}/100

**Device Breakdown**: ${deviceTypes || 'None'}

**Operating Systems**: ${osTypes || 'None'}

**Cloud Providers**: ${cloudProviders || 'None'}`;
    }

    /**
     * Format anomalies as natural language
     */
    formatAnomalies(anomalies: Anomaly[]): string {
        if (anomalies.length === 0) {
            return `**No anomalies detected!** All devices appear to be consistent with their peers.`;
        }

        let response = `**Found ${anomalies.length} Anomalies**\n\n`;

        anomalies.forEach((anomaly, index) => {
            response += `**${index + 1}. ${anomaly.device_name}**\n`;
            response += `   - Anomaly Score: ${(anomaly.anomaly_score * 100).toFixed(1)}%\n`;
            response += `   - Peer Similarity: ${(anomaly.average_similarity * 100).toFixed(1)}%\n`;
            response += `   - Reasons:\n`;
            anomaly.reasons.forEach(reason => {
                response += `     * ${reason}\n`;
            });
            response += `\n`;
        });

        return response;
    }

    /**
     * Format security findings as natural language
     */
    formatSecurity(findings: SecurityFinding[]): string {
        if (findings.length === 0) {
            return `**No security violations found!** Your infrastructure passes compliance checks.`;
        }

        const severityCounts = this.countBySeverity(findings);

        let response = `**Security Audit Results**\n\n`;
        response += `Found ${findings.length} security findings:\n`;
        Object.entries(severityCounts).forEach(([severity, count]) => {
            response += `   - ${severity}: ${count}\n`;
        });
        response += `\n**Details:**\n\n`;

        findings.slice(0, 5).forEach((finding, index) => {
            const severity = Object.keys(finding.severity)[0];
            response += `**${index + 1}. ${finding.title}** (${severity})\n`;
            response += `   ${finding.description}\n`;
            response += `   Affected: ${finding.affected_resources.join(', ')}\n\n`;
        });

        if (findings.length > 5) {
            response += `_...and ${findings.length - 5} more findings_\n`;
        }

        return response;
    }

    /**
     * Format cost recommendations as natural language
     */
    formatCost(recommendations: CostRecommendation[]): string {
        if (recommendations.length === 0) {
            return `**No cost optimization opportunities found** at this time. Your infrastructure appears to be well-optimized!`;
        }

        let response = `**Cost Optimization Opportunities**\n\n`;
        response += `Found ${recommendations.length} potential savings:\n\n`;

        recommendations.forEach((rec, index) => {
            const priority = Object.keys(rec.priority)[0];
            response += `**${index + 1}. ${rec.title}** (${priority} priority)\n`;
            response += `   ${rec.description}\n`;
            response += `   **Estimated Impact**: ${rec.estimated_impact}\n`;
            response += `   **Action Items**:\n`;
            rec.action_items.forEach(item => {
                response += `     * ${item}\n`;
            });
            response += `\n`;
        });

        return response;
    }

    /**
     * Count findings by severity
     */
    private countBySeverity(findings: SecurityFinding[]): Record<string, number> {
        return findings.reduce((acc, f) => {
            const sev = Object.keys(f.severity)[0];
            acc[sev] = (acc[sev] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);
    }
}
